import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Slider,
  CircularProgress,
  Chip,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useParams, useNavigate } from "react-router-dom";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LabelIcon from "@mui/icons-material/Label";
import InfoIcon from "@mui/icons-material/Info";
import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

// 视频容器 - 移动端优化
const VideoContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  paddingBottom: "56.25%", // 16:9
  height: 0,
  position: "relative",
  borderRadius: "12px",
  overflow: "hidden",
  backgroundColor: "#000",
  marginTop: theme.spacing(1.5),
  "& iframe": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    border: "none",
  },
}));

// 音频播放器容器 - 移动端优化
const AudioPlayerContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(2),
  background: theme.palette.mode === "dark"
    ? "rgba(28, 28, 30, 0.4)"
    : "rgba(255, 255, 255, 0.6)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  borderRadius: "12px",
  border: theme.palette.mode === "dark"
    ? "0.5px solid rgba(84, 84, 88, 0.5)"
    : "0.5px solid rgba(0, 0, 0, 0.08)",
  marginTop: theme.spacing(1.5),
}));

// 音频控制按钮
const AudioControlButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.mode === "dark" ? "#ffffff" : "#000000",
  fontSize: "3rem",
  padding: theme.spacing(1),
}));

// 音频进度条
const AudioSlider = styled(Slider)(({ theme }) => ({
  color: theme.palette.mode === "dark" ? "#ffffff" : "#000000",
  height: 4,
  "& .MuiSlider-track": {
    border: "none",
  },
  "& .MuiSlider-thumb": {
    width: 14,
    height: 14,
    backgroundColor: theme.palette.mode === "dark" ? "#ffffff" : "#000000",
  },
  "& .MuiSlider-rail": {
    opacity: 0.3,
  },
}));

// 获取 YouTube 视频 ID
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const cleanUrl = url.split("?")[0];
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = cleanUrl.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

const MobileVisionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchActivityDetails = async () => {
      if (!id) {
        setDetail(null);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(
          `${API_BASE_URL}/vision-space/${id}`
        );
        setDetail(response.data);
      } catch (err) {
        console.error("获取活动详情失败:", err);
        setError("获取活动详情失败，请稍后再试");
      } finally {
        setLoading(false);
      }
    };

    fetchActivityDetails();
  }, [id]);

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSliderChange = (_, value) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value;
      setCurrentTime(value);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const textColor = prefersDarkMode ? "#ffffff" : "#000000";
  const secondaryTextColor = prefersDarkMode ? "rgba(235, 235, 245, 0.6)" : "rgba(60, 60, 67, 0.6)";
  const borderColor = prefersDarkMode ? "rgba(84, 84, 88, 0.65)" : "rgba(0, 0, 0, 0.1)";

  // 处理媒体URL
  const videoId = detail?.mediaType === "video" ? getYouTubeVideoId(detail.mediaUrl || detail.videoUrl) : null;
  const audioUrl = detail?.mediaType === "audio" 
    ? (detail.audioUrl?.startsWith("http") 
        ? detail.audioUrl 
        : `${API_BASE_URL}${detail.audioUrl}`)
    : null;

  if (loading) {
    return (
      <Box sx={{ px: 2, py: 2.5, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress size={24} sx={{ color: textColor }} />
      </Box>
    );
  }

  if (error || !detail) {
    return (
      <Box sx={{ px: 2, py: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ color: textColor, mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 600, color: textColor }}>
            返回
          </Typography>
        </Box>
        <Typography sx={{ color: secondaryTextColor, fontSize: "0.85rem", textAlign: "center", py: 4 }}>
          {error || "未找到活动详情"}
        </Typography>
      </Box>
    );
  }

  const date = new Date(detail.date);
  const isUpcoming = detail.status === "upcoming";
  const now = new Date();
  const daysUntil = Math.ceil((date - now) / (1000 * 60 * 60 * 24));

  return (
    <Box sx={{ px: 2, py: 2.5, pb: 4 }}>
      {/* 返回按钮和标题 */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ color: textColor, mr: 1, padding: 0.5 }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ fontSize: "1.05rem", fontWeight: 600, color: textColor }}>
          {detail.title}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: borderColor, mb: 2 }} />

      {/* 日期和状态 */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <AccessTimeIcon sx={{ fontSize: "0.75rem", color: secondaryTextColor }} />
          <Typography sx={{ fontSize: "0.75rem", color: secondaryTextColor }}>
            {date.toLocaleDateString()}
          </Typography>
        </Box>
        {isUpcoming && (
          <Typography sx={{ fontSize: "0.75rem", color: secondaryTextColor }}>
            · 还有 {daysUntil} 天
          </Typography>
        )}
        {detail.mediaType && (
          <Typography sx={{ fontSize: "0.75rem", color: secondaryTextColor }}>
            · {detail.mediaType === "video" ? "视频" : detail.mediaType === "audio" ? "音频" : "无媒体"}
          </Typography>
        )}
        {detail.type && (
          <Typography sx={{ fontSize: "0.75rem", color: secondaryTextColor }}>
            · {detail.type}
          </Typography>
        )}
      </Box>

      {/* 描述 */}
      {detail.description && (
        <Typography
          sx={{
            fontSize: "0.85rem",
            color: textColor,
            lineHeight: 1.6,
            whiteSpace: "pre-line",
            mb: 2,
          }}
        >
          {detail.description}
        </Typography>
      )}

      {/* 作者信息 */}
      {detail.authors && detail.authors.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: "0.7rem", color: secondaryTextColor, mb: 0.75 }}>
            作者
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {detail.authors.map((author) => (
              <Chip
                key={author._id}
                label={author.name || "未知作者"}
                size="small"
                sx={{
                  fontSize: "0.7rem",
                  height: "24px",
                  backgroundColor: prefersDarkMode
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.04)",
                  color: textColor,
                  border: `0.5px solid ${borderColor}`,
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      <Divider sx={{ borderColor: borderColor, mb: 2 }} />

      {/* 视频播放器 */}
      {detail.mediaType === "video" && videoId && (
        <Box>
          <VideoContainer>
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?playsinline=1&rel=0&modestbranding=1`}
              title={detail.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              playsInline
            />
          </VideoContainer>
        </Box>
      )}

      {/* 音频播放器 */}
      {detail.mediaType === "audio" && audioUrl && (
        <AudioPlayerContainer>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
            <AudioControlButton onClick={handlePlayPause}>
              {isPlaying ? (
                <PauseCircleOutlineIcon sx={{ fontSize: "3rem" }} />
              ) : (
                <PlayCircleOutlineIcon sx={{ fontSize: "3rem" }} />
              )}
            </AudioControlButton>
            <Box sx={{ width: "100%", px: 1 }}>
              <AudioSlider
                value={currentTime}
                max={duration || 0}
                onChange={handleSliderChange}
                size="small"
              />
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                <Typography sx={{ fontSize: "0.7rem", color: secondaryTextColor }}>
                  {formatTime(currentTime)}
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: secondaryTextColor }}>
                  {formatTime(duration)}
                </Typography>
              </Box>
            </Box>
            <audio
              ref={audioRef}
              src={audioUrl}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={() => setIsPlaying(false)}
              onError={(e) => {
                console.error("音频加载错误:", e);
                setError("音频文件无法加载");
              }}
            />
          </Box>
        </AudioPlayerContainer>
      )}

      {/* 内容 */}
      {detail.content && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <InfoIcon sx={{ fontSize: "0.85rem", color: secondaryTextColor }} />
            <Typography sx={{ fontSize: "0.75rem", color: secondaryTextColor }}>
              详细内容
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: "0.85rem",
              color: textColor,
              lineHeight: 1.6,
              whiteSpace: "pre-line",
              pl: 2,
            }}
          >
            {detail.content}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MobileVisionDetail;

